const DETECT_FRAME_MILLI_SEC = 10; // 発話検知確認の1フレーム長(msec)
const DEFAULT_DETECT_LENGTH_MILLI_SEC = 170; //サーバー側は150だがより条件を厳しくするために170とする
const SILENT_LENGTH_MILLI_SEC = 300; // 発話終了とみなす時間(msec)
const DEFAULT_ANALYZE_LENGTH_MILLI_SEC = 0; // 音声解析に必要な発話時間(msec)

function countLastConsecutiveTrues(list: boolean[]): number {
  let count = 0;
  for (let i = list.length - 1; i >= 0; i--) {
    if (list[i]) count++;
    else break;
  }
  return count;
}

function getOverThresholdTime(
  volumes: Float32Array,
  threshVolume?: number,
  lengthMsec?: number,
  currentOverThresholdTime?: number,
  detectLengthMSec: number = DEFAULT_DETECT_LENGTH_MILLI_SEC,
  useMaxVolume?: boolean,
): {
  time: number;
  sum: number;
} {
  const sumDefault = currentOverThresholdTime ? currentOverThresholdTime : 0;
  if (!threshVolume || !lengthMsec) {
    return {
      time: 0,
      sum: sumDefault,
    };
  }

  // ボリュームゲージの反応が過敏になる為、発話中とみなした後は判定基準を緩和する
  if (
    currentOverThresholdTime &&
    currentOverThresholdTime >= detectLengthMSec
  ) {
    return {
      time: sumDefault + lengthMsec,
      sum: sumDefault + lengthMsec,
    };
  }

  const overThresholdList = [];
  const checkVolumeMsecSpan = lengthMsec / DETECT_FRAME_MILLI_SEC;
  const checkVolumeSpan = volumes.length / checkVolumeMsecSpan;

  for (let i = 0; i < volumes.length; i += checkVolumeSpan) {
    const s = volumes.slice(i, i + checkVolumeSpan);

    let tgtVolume = 0;
    if (useMaxVolume) {
      // 最大音量を計算
      const spanMax = Math.max(...s);
      tgtVolume = spanMax * 12800;
    } else {
      // 平均音量を計算
      const spanAverage = s.reduce((sum, v) => sum + v, 0) / s.length;
      tgtVolume = spanAverage * Math.pow(2, 15);
    }
    overThresholdList.push(tgtVolume > threshVolume);
  }

  // 全て発話検知していた場合
  if (overThresholdList.every((v) => v)) {
    return {
      time: sumDefault + lengthMsec,
      sum: sumDefault + lengthMsec,
    };
  }

  const lastTrueCount = countLastConsecutiveTrues(overThresholdList);
  // 途中～最後まで発話検知していた場合
  if (overThresholdList[overThresholdList.length - 1]) {
    return {
      time: lastTrueCount * DETECT_FRAME_MILLI_SEC,
      sum: lastTrueCount * DETECT_FRAME_MILLI_SEC,
    };
  }

  // 途中だけ発話検知していた場合
  let maxConsecutiveTrues = 0;
  let currentCount = 0;
  overThresholdList.forEach((value) => {
    if (value) {
      currentCount++;
      if (currentCount > maxConsecutiveTrues) {
        maxConsecutiveTrues = currentCount;
      }
    } else {
      currentCount = 0;
    }
  });

  return {
    time: maxConsecutiveTrues * DETECT_FRAME_MILLI_SEC,
    sum: 0,
  };
}

export function calcPower(
  input: Float32Array,
  threshVolume?: number,
  lengthMsec?: number,
  currentOverThresholdTime?: number,
  detectLengthMSec: number = DEFAULT_DETECT_LENGTH_MILLI_SEC,
  useMaxVolume?: boolean,
): [number, number, boolean, number] {
  const volumes = input.map((volume) => {
    const s = Math.max(-1, Math.min(1, volume));
    return Math.abs(s);
  });

  const sumVolume = volumes.reduce((sum, volume) => {
    return sum + volume;
  }, 0);
  const avgPower = 0 < volumes.length ? sumVolume / volumes.length : 0;
  const maxPower = Math.max(...volumes);

  const { time: overThresholdTime, sum: overThresholdTimeSum } =
    getOverThresholdTime(
      volumes,
      threshVolume,
      lengthMsec,
      currentOverThresholdTime,
      detectLengthMSec,
      useMaxVolume,
    );
  const overThresholdTimeFlag = overThresholdTime >= detectLengthMSec;
  return [avgPower, maxPower, overThresholdTimeFlag, overThresholdTimeSum];
}

export function detectVoice(
  s: Float32Array,
  sr: number,
  threshVolume: number,
  detectLengthMSec: number = DEFAULT_DETECT_LENGTH_MILLI_SEC,
): boolean {
  // 1フレームのインデックス長
  const frameSize = Math.floor((sr * DETECT_FRAME_MILLI_SEC) / 1000);

  // 音声と判定するための音声の閾値越えが継続しているフレーム数
  const detectFrameLength = Math.floor(
    detectLengthMSec / DETECT_FRAME_MILLI_SEC,
  );

  // 音量閾値を超えているかを示すフレーム毎のリスト
  const frameVolumeOverList: boolean[] = [];

  for (let i = 0; i < Math.floor(s.length / frameSize) - 1; ++i) {
    // フレームを取得
    const frame = s.slice(i * frameSize, (i + 1) * frameSize);
    // フレームの最大音量を取得
    const absFrame = frame.map((v) => Math.abs(v));
    const averageVolume =
      (absFrame.reduce((a, b) => a + b, 0) / Math.max(absFrame.length, 1)) *
      2 ** 15;

    // フレームが閾値を超えているかをリストに追加
    frameVolumeOverList.push(averageVolume > threshVolume);

    // 過去フレームが連続して閾値を超えていれば発話とする
    if (frameVolumeOverList.length >= detectFrameLength) {
      const overListOfDetectLength =
        frameVolumeOverList.slice(-detectFrameLength);
      if (overListOfDetectLength.every((v) => v)) {
        return true;
      }
    }
  }
  return false;
}

export function detectVoiceV2(
  samples: Float32Array,
  sr: number,
  threshVolume: number,
  detectLengthMSec: number = DEFAULT_DETECT_LENGTH_MILLI_SEC,
  analyzeLengthMSec: number = DEFAULT_ANALYZE_LENGTH_MILLI_SEC,
  analyzeMode = false,
): [boolean, number[], number[][]] {
  let detectResult = false;

  // 1フレームのインデックス長
  const frameSize = Math.floor((sr * DETECT_FRAME_MILLI_SEC) / 1000);

  // 音声と判定するための音声の閾値越えが継続しているフレーム数
  const detectFrameLength = Math.floor(
    detectLengthMSec / DETECT_FRAME_MILLI_SEC,
  );

  // 音声解析に必要な発話区域のフレーム数
  const minAnalyzeFrameLength = Math.floor(
    analyzeLengthMSec / DETECT_FRAME_MILLI_SEC,
  );

  // 発話終了とするフレーム数
  const silentFrameLength = Math.floor(
    SILENT_LENGTH_MILLI_SEC / DETECT_FRAME_MILLI_SEC,
  );

  // フレーム毎の最大音量値のリスト
  const maxVolumeList: number[] = [];

  // 発話検知区間
  let voiceDetectionSegment: number[] = [];

  // 発話検知区間のリスト
  const voiceDetectionSegmentList: number[][] = [];

  // 音量閾値を超えているかを示すフレーム毎のリスト
  // 0: 発話無し 1: 発話検知 2: 発話中 3: 発話中無音 4: 発話終了
  const frameDetectVoiceStatusList: number[] = [];

  let voiceDetectCount = 0;
  let latestDetectVoiceFrame = 0;

  const absFrames: number[][] = samples.reduce((frame, sample, index) => {
    const i = Math.floor(index / frameSize);
    const j = index % frameSize;

    if (!frame[i]) {
      frame[i] = new Array(frameSize);
      frame[i].fill(0);
    }
    frame[i][j] = Math.abs(sample);
    return frame;
  }, [] as number[][]);

  absFrames.forEach((absFrame, i) => {
    // フレームの最大音量を取得
    const maxVolume = Math.max(...absFrame);

    maxVolumeList.push(maxVolume);
    const calcMaxValue = Math.floor(maxVolume * 12800);

    // フレームが閾値を超えているかをリストに追加
    let detectVoiceStatus = 0;
    if (calcMaxValue > threshVolume) {
      detectVoiceStatus = 1;
      latestDetectVoiceFrame = i;
    }

    const lastVoiceStatus = frameDetectVoiceStatusList.slice(-1)[0] ?? 0;

    // 発話検知が規定時間連続したら発話中
    if (
      detectVoiceStatus === 1 &&
      i >= detectFrameLength &&
      frameDetectVoiceStatusList
        .slice(-detectFrameLength)
        .every((value) => value === 1)
    ) {
      detectVoiceStatus = 2;
      const startTime = i - detectFrameLength;
      voiceDetectionSegment = [startTime];
    }

    // 発話中に音声を検知したら発話中を継続
    // 発話中無音で発話を検知したら発話中に戻す
    if (
      (lastVoiceStatus === 2 || lastVoiceStatus === 3) &&
      detectVoiceStatus === 1
    ) {
      detectVoiceStatus = 2;
    }

    // 発話中に音声を検知出来なかったら発話中無音
    if (
      i > 1 &&
      (lastVoiceStatus === 2 || lastVoiceStatus === 3) &&
      detectVoiceStatus === 0
    ) {
      detectVoiceStatus = 3;
    }

    // 発話中無音が規定時間連続したら発話終了
    if (
      detectVoiceStatus === 3 &&
      i >= silentFrameLength &&
      frameDetectVoiceStatusList
        .slice(-silentFrameLength)
        .every((value) => value === 3)
    ) {
      detectVoiceStatus = 4;
      const endTime = i - silentFrameLength;
      voiceDetectionSegment.push(endTime);
      voiceDetectionSegmentList.push(voiceDetectionSegment);
      voiceDetectionSegment = [];
    }

    frameDetectVoiceStatusList.push(detectVoiceStatus);

    switch (detectVoiceStatus) {
      case 0:
        voiceDetectCount = 0;
        break;
      case 1:
        voiceDetectCount++;
        break;
      case 2:
        voiceDetectCount++;
        if (voiceDetectCount >= minAnalyzeFrameLength) {
          detectResult = true;
        }
        break;
      case 3:
        voiceDetectCount++;
        break;
      case 4:
        voiceDetectCount = 0;
        break;
    }

    // analyzeModeでなければ発話検知した時点で終了
    if (detectResult && !analyzeMode) {
      return [detectResult, maxVolumeList, voiceDetectionSegmentList];
    }
  });

  const lastDetectVoiceStatus =
    frameDetectVoiceStatusList[frameDetectVoiceStatusList.length - 1];
  // 発話中に録音終了したら強制的に発話終了（最後に発話検知した所まで）
  if (lastDetectVoiceStatus === 2 || lastDetectVoiceStatus === 3) {
    voiceDetectionSegment.push(latestDetectVoiceFrame);
    voiceDetectionSegmentList.push(voiceDetectionSegment);
  }
  return [detectResult, maxVolumeList, voiceDetectionSegmentList];
}
